import {
  Modal,
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
  TextInput,
} from 'carbon-components-react';

import '../daily-fee.styles.scss';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import Combobox from '../../../components/combobox/Combobox';
import {
  formatCurrencyV2,
  getQueryString,
  hasExpired,
  OPTIONS,
  today,
  useReducerSpread,
} from '../../../utils/common.util';
import useSWR from 'swr';
import { useCustomMutation } from '../../../utils/useCustomMutation';
import { postMethod } from '../../../api/common';
import { useDebounceState } from '../../../utils/useDebounceState';
import { RenderIf } from '../../../components/RenderIf';
import ComponentGuard from '../../../components/component-guard/ComponentGuard';
import { useEffect } from 'react';

const initialData = {
  amountPaid: '',
  debitAccountId: '',
  paymentDate: today,
  description: '',
  paymentMode: '',
  receivedFrom: '',
  studentId: '',
  dailyBillingFeeId: '',
  chequeNumber: '',
  bankersDraftNumber: '',
  bankTransferNumber: '',
};

export default function MakeArrearsPayment({
  isOpen,
  closeModal,
  currentStudentData,
  onSuccess,
}) {
  const [formData, setFormData] = useReducerSpread(initialData);

  const { data: ledgerAccounts } = useSWR('chart-of-accounts/ledger-accounts');

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: s, isLoading: isStudentsLoading } = useSWR(
    `daily-billing/fees/assigned-students?${getQueryString({
      query: searchStudent,
      dailyBillingFeeId: formData.dailyBillingFeeId,
    })}`,
  );
  const students = s?.response ?? [];

  const { data } = useSWR(`daily-billing/fees/list`);
  const feeTypes = data?.dailyBillingFee ?? [];

  // fetch unpaid bills
  const { data: response } = useSWR(
    formData.studentId && formData.dailyBillingFeeId && !currentStudentData
      ? `daily-billing/fees/student-arrears?${getQueryString({
          studentId: formData.studentId,
          dailyBillingFeeId: formData.dailyBillingFeeId,
        })}`
      : null,
  );
  const results = response?.result ?? [];
  const studentData = results?.[0] ?? currentStudentData ?? null;

  useEffect(() => {
    if (studentData) {
      setFormData({
        studentId: studentData?.student?.id,
        dailyBillingFeeId: studentData?.dailyBillingFee?.id,
      });
    }
  }, [studentData, setFormData]);

  const { trigger, isMutating, error, reset } = useCustomMutation(
    `daily-billing/revenue/pay-arrears`,
    (url, { arg }) => postMethod(url, arg),
  );

  function handleSubmit(e) {
    e.preventDefault();
    trigger(formData, {
      onSuccess: (data) => {
        onSuccess?.(data?.data?.payment);
        handleClose();
      },
    });
  }

  function handleClose() {
    setFormData(initialData);
    closeModal();
    reset();
  }

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  return (
    <Modal
      id="create-arrears-modal"
      className="big-modal"
      hasScrollingContent
      passiveModal={true}
      open={isOpen}
      modalHeading="Make Arrears Payment"
      primaryButtonDisabled={false}
    >
      <form onSubmit={handleSubmit}>
        <div className="card-row-2 gap-20 mb-20">
          <Select
            labelText="Fee Type"
            disabled={!!currentStudentData}
            value={formData.dailyBillingFeeId}
            onChange={(e) =>
              setFormData({ dailyBillingFeeId: e.target.value, studentId: '' })
            }
          >
            <SelectItem selected value="" text="" />
            {feeTypes?.map((item) => (
              <SelectItem
                key={item.id}
                text={
                  item.name +
                  (isDisabled(item) && item?.invoice?.status
                    ? ` - [${item.invoice.status}]`
                    : '')
                }
                value={item.id}
                disabled={isDisabled(item)}
              />
            ))}
          </Select>
          <Combobox
            options={students ?? []}
            label="Student"
            disabled={!formData.dailyBillingFeeId || !!currentStudentData}
            value={
              formData.studentId
                ? students?.find((s) => s.id === formData.studentId)
                : null
            }
            onChange={(option) => {
              setFormData({ studentId: option ? option.id : '' });
              setSearchStudent('');
            }}
            onSearch={setSearchStudent}
            onClear={() => {
              setFormData({ studentId: '' });
              setSearchStudent('');
            }}
            loading={isStudentsLoading}
            renderSelected={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            renderOption={(option) => `${option.firstName} ${option.lastName}`}
            labelKey="fullName"
            valueKey="id"
            searchPlaceholder="Search students..."
            placeholder=""
          />
        </div>

        {/* <table className="bx--data-table table-white my-20">
          <thead>
            <tr>
              <th>Total Amount Billed</th>
              <th>Total Amount Paid</th>
              <th>Total Balance Due</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{studentData?.totalAmountBilled ?? 'N/A'}</td>
              <td>{studentData?.totalAmountPaid ?? 'N/A'}</td>
              <td>{studentData?.totalBalance ?? 'N/A'}</td>
            </tr>
          </tbody>
        </table> */}
        <RenderIf condition={studentData}>
          <div className="d-flex-space-between mt-10 mb-40 text-16">
            <div>
              <span className="font-500">Total Amount Billed: </span>
              {formatCurrencyV2(studentData?.totalAmountBilled) ?? 'N/A'}
            </div>
            <div>
              <span className="font-500">Total Amount Paid: </span>
              {formatCurrencyV2(studentData?.totalAmountPaid) ?? 'N/A'}
            </div>
            <div className="font-bold">
              <span className="font-500">Total Balance Due: </span>
              {formatCurrencyV2(studentData?.totalBalance) ?? 'N/A'}
            </div>
          </div>
        </RenderIf>

        <div className="card-row-2 gap-20">
          <TextInput
            name="amount"
            labelText="Amount"
            placeholder="Enter Amount"
            type={'number'}
            value={formData.amountPaid}
            onChange={(e) => {
              const inputValue = parseFloat(e.target.value);
              const maxAmount = studentData?.totalBalance ?? 0;
              const newAmount =
                inputValue < 0
                  ? 0
                  : inputValue > maxAmount
                    ? maxAmount
                    : inputValue;
              setFormData({ amountPaid: newAmount });
            }}
          />
          <ComponentGuard allowedRoles={['RevenueBackDatingAccess']}>
            <DatePicker
              dateFormat="Y-m-d"
              datePickerType="single"
              id="arrears-date-picker"
              maxDate={new Date().toISOString().split('T')[0]}
              onChange={(value) => {
                if (value.length > 0) {
                  setFormData({
                    paymentDate: [
                      value[0].getFullYear(),
                      ('0' + (value[0].getMonth() + 1)).slice(-2),
                      ('0' + value[0].getDate()).slice(-2),
                    ].join('-'),
                  });
                } else setFormData({ paymentDate: '' });
              }}
              onClose={(value) => {}}
            >
              <DatePickerInput
                className="payment-date"
                id="payment-date"
                name="paymentDate"
                labelText="Payment Date"
                value={formData.paymentDate}
                max={new Date().toISOString().split('T')[0]}
                pattern="\d{4}-\d{2}-\d{2}"
                autoComplete="off"
                placeholder="YYYY-MM-DD"
                type="text"
                onChange={(e) => setFormData({ paymentDate: e.target.value })}
              />
            </DatePicker>
          </ComponentGuard>

          <Select
            id="debitAccount"
            labelText="Debit Account (Cash or Bank Account)"
            value={formData.debitAccountId}
            onChange={(e) => setFormData({ debitAccountId: e.target.value })}
          >
            <SelectItem selected text="" value="" />
            {ledgerAccounts
              ?.filter(
                (currentAsset) =>
                  currentAsset.categoryDescription === 'Bank Accounts' ||
                  currentAsset.categoryDescription === 'Cash Accounts',
              )
              .map((currentAsset) => (
                <SelectItem
                  key={currentAsset.id}
                  text={currentAsset.description}
                  value={currentAsset.id}
                />
              ))}
          </Select>

          <Select
            labelText="Payment Mode"
            value={formData.paymentMode}
            onChange={(e) => setFormData({ paymentMode: e.target.value })}
          >
            <SelectItem disabled text="" value="" />
            {OPTIONS.paymentModes
              .filter((mode) => !mode.label.toLowerCase().includes('deposit'))
              .map((mode) => (
                <SelectItem
                  key={mode.value}
                  value={mode.value}
                  text={mode.label}
                />
              ))}
          </Select>
          <RenderIf condition={formData.paymentMode === 'Cheque'}>
            <TextInput
              id="cheque-number"
              labelText="Cheque Number"
              value={formData.chequeNumber}
              onChange={(e) => setFormData({ chequeNumber: e.target.value })}
              placeholder="Enter Cheque Number"
            />
          </RenderIf>
          <RenderIf condition={formData.paymentMode === 'BankersDraft'}>
            <TextInput
              id="bankers-draft-number"
              labelText="Bankers Draft Number"
              value={formData.bankersDraftNumber}
              onChange={(e) =>
                setFormData({ bankersDraftNumber: e.target.value })
              }
              placeholder="Enter Bankers Draft Number"
            />
          </RenderIf>
          <RenderIf condition={formData.paymentMode === 'Bank_Transfer'}>
            <TextInput
              id="bank-transfer-number"
              labelText="Bank Transfer Number"
              value={formData.bankTransferNumber}
              onChange={(e) =>
                setFormData({ bankTransferNumber: e.target.value })
              }
              placeholder="Enter Bank Transfer Number"
            />
          </RenderIf>

          <TextInput
            id="receivedFrom"
            name="receivedFrom"
            labelText="Received From"
            placeholder="Enter who you received the money from"
            value={formData.receivedFrom}
            onChange={(e) => setFormData({ receivedFrom: e.target.value })}
          />

          <TextInput
            name="description"
            labelText="Description"
            placeholder="Enter description"
            value={formData.description}
            onChange={(e) => setFormData({ description: e.target.value })}
          />
        </div>

        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}

        <div
          style={{
            marginTop: '30px',
            display: 'flex',
            gap: '20px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <CustomButton
            classes="outline danger no-hover font-bold"
            style={{ flex: '1' }}
            onClick={handleClose}
          >
            CLOSE
          </CustomButton>

          <CustomButton
            type="submit"
            style={{ flex: '1' }}
            classes={`primary font-bold`}
            isLoading={isMutating}
            disabled={
              !formData.amountPaid ||
              !formData.debitAccountId ||
              !formData.paymentDate ||
              !formData.paymentMode ||
              !formData.receivedFrom ||
              !formData.studentId ||
              !formData.dailyBillingFeeId ||
              (formData.paymentMode === 'Cheque' && !formData.chequeNumber) ||
              (formData.paymentMode === 'BankersDraft' &&
                !formData.bankersDraftNumber) ||
              (formData.paymentMode === 'Bank_Transfer' &&
                !formData.bankTransferNumber)
            }
          >
            Submit Payment
          </CustomButton>
        </div>
      </form>
    </Modal>
  );
}
